import React, { useCallback, useMemo, useState } from "react";
import {
  View,
  Text,
  StyleSheet,
  FlatList,
  Platform,
  useWindowDimensions,
} from "react-native";
import { MaterialIcons } from "@expo/vector-icons";
import { TextInput } from "react-native-paper";
import { useTranslation } from "react-i18next";
import { useNavigation } from "@react-navigation/native";
import { ROUTES } from "../../shared/constants/routes";
import { useThemeSettings } from "../../ThemeProvider";
import getGlobalStyles from "../../globalStyles";
import { CollectionBubble } from "../../components/CollectionBubble";
import { GradientStrokeBox } from "../../components/GradientStrokeBox";
import { CustomButton, ButtonVariant } from "../../components/CustomButton";
import { saveCollection } from "../../services/apiService";
import { trackInteraction } from "../../analytics/analyticsUtil";
import { ACTION_TYPE, ANALYTICS_EVENT } from "../../analytics/types";
import { RecommendedCollection } from "../../shared/types/DataTypes";
import { MODAL_MAX_WIDTH } from "../../shared/constants/dimension";
import { useSafeAreaInsets } from "react-native-safe-area-context";
import CustomLinearGradient, { GRADIENT_TYPE } from "../../components/CustomLinearGradient";
import { usePlatform } from "../../providers/PlatformProvider";
import { removeCollectionFromCache, useRecommendations } from "../../services/tanStackService";
import { queryClient } from "../../services/tanStackClient";
import { useCollectionStore } from "../../store/collectionStore";
import { useActiveTabStore } from "../../store/activeTabStore";
import AppLoader from "../../components/AppLoader";
import AppBackground from "../../components/AppBackground";

const GRID_COLUMNS = 4;
const SAVE_FOOTER_RESERVED = 88;

function mergeTagsFromCollections(collections: RecommendedCollection[]): any[] {
  const map = new Map<string, any>();
  for (const c of collections) {
    const tags = (c as any).SeoTags || (c as any).tags || [];
    for (const t of tags) {
      map.set(String(t.id), t);
    }
  }
  return Array.from(map.values());
}
function getCollectionAndTagId(collection: any): string | undefined {
  return collection?.AndTag?.id ?? collection?.andTag?.id ?? undefined;
}

export default function RecommendedCollectionsScreen() {
  const { t } = useTranslation();
  const { theme } = useThemeSettings();
  const { showWebVersion } = usePlatform();
  const gStyles = useMemo(() => getGlobalStyles(theme), [theme]);
  const insets = useSafeAreaInsets();
  const { width: windowWidth, height: windowHeight } = useWindowDimensions();
  const navigation = useNavigation<any>();

  const activetab = useActiveTabStore((s) => s.activetab);
  const { collectionList, setCollectionList, setSelectedCollection, setReload } =
    useCollectionStore();

  const [search, setSearch] = useState("");
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [isSaving, setIsSaving] = useState(false);

  const { data: recommendations, isLoading } = useRecommendations(40);
  const collections: RecommendedCollection[] = recommendations ?? [];

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return collections;
    return collections.filter((c: any) => {
      const title = String(c.title ?? "").toLowerCase();
      const tags = c.SeoTags || c.tags || [];
      const tagMatch = tags.some((tag: any) =>
        String(tag.name ?? tag.label ?? "").toLowerCase().includes(q)
      );
      return title.includes(q) || tagMatch;
    });
  }, [collections, search]);

  const toggleSelect = useCallback((collection: any) => {
    if (isSaving) return;
    const id = String(collection.id);
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, [isSaving]);

  const selectedCollections = useMemo(() => {
    return collections.filter((c: any) => selectedIds.has(String(c.id)));
  }, [collections, selectedIds]);

  const mergedTags = useMemo(
    () => mergeTagsFromCollections(selectedCollections),
    [selectedCollections]
  );

  const canSave = mergedTags.length > 0;

  const onSave = useCallback(async () => {
    if (!canSave || isSaving) return;
    setIsSaving(true);
    try {
      const saved: any[] = [];
      const savedSourceIds: string[] = [];
      for (const c of selectedCollections) {
        const tags = (c as any).SeoTags || (c as any).tags || [];
        const tagIds = tags.map((t: any) => t.id).filter(Boolean);
        if (!tagIds.length) continue;

        const newCollection = await saveCollection({
          title: (c as any).title,
          tags: tagIds,
          andTag: getCollectionAndTagId(c),
        });
        saved.push(newCollection);
        savedSourceIds.push(String((c as any).id));
      }

      if (saved.length === 0) return;

      let nextList = [...collectionList];
      for (const created of saved) {
        nextList = nextList.filter((x: any) => x.id !== created.id);
        nextList.push(created);
      }
      setCollectionList(nextList);
      setSelectedCollection(saved[saved.length - 1]);
      setReload(true);

      trackInteraction({
        event: ANALYTICS_EVENT.INTERACTION,
        action: ACTION_TYPE.CLICK,
        screen_name: ROUTES.RECOMMENDED_COLLECTIONS,
        component: "RecommendedCollectionsSave",
        context: {
          flow: "recommended_picker",
          saved_count: saved.length,
        },
      });
      navigation.navigate(ROUTES.HOME, { selectedTab: activetab });
      for (const id of savedSourceIds) {
        removeCollectionFromCache(id);
      }
      await queryClient.invalidateQueries({ queryKey: ["recommendations"] });
    } catch (e) {
      console.error("[RecommendedCollections] save failed:", e);
    } finally {
      setIsSaving(false);
    }
  }, [
    canSave,
    isSaving,
    selectedCollections,
    collectionList,
    setCollectionList,
    setSelectedCollection,
    setReload,
    navigation,
    activetab,
  ]);

  const horizontalPad = 20;
  const cardWidth  =
    Platform.OS === "web"
    ? Math.min(windowWidth, Number(MODAL_MAX_WIDTH))
      : windowWidth;
  const innerRowWidth = cardWidth - horizontalPad * 2;
  const gap = 8;
  const cellWidth =
    (innerRowWidth - gap * (GRID_COLUMNS - 1)) / GRID_COLUMNS;

  const renderItem = useCallback(
    ({ item, index }: { item: RecommendedCollection; index: number }) => {
      const selected = selectedIds.has(String((item as any).id));
      return (
        <View style={[styles.cell, { width: cellWidth }]}>
          <View style={[styles.bubbleWithBadge, isSaving && { opacity: 0.4 }]}>
            <CollectionBubble
              collection={item as any}
              index={index}
              showTopRightIcon={false}
              isSelected={selected}
              isDisabled={isSaving}
              onBubblePress={() => toggleSelect(item)}
            />
            {selected && (
              <View
                pointerEvents="none"
                style={[
                  styles.selectionBadge,
                  { borderColor: theme.colors.white, backgroundColor : "#09671D" },
                ]}
              >
                <MaterialIcons
                  name="check"
                  size={20}
                  color={theme.colors.white}
                />
              </View>
            )}
          </View>
        </View>
      );
    },
    [cellWidth, selectedIds, toggleSelect, theme.colors.surfaceColor, theme.colors.success, isSaving]
  );

  return (
<AppBackground>
      <View style={styles.contentSheet}>
          <View style={styles.searchSection}>
            <View style={styles.searchInputWrap}>
              <GradientStrokeBox
                stroke={2}
                wrapperStyle={styles.searchGradientWrap}
              >
                <View style={styles.searchInputInner}>
                  <TextInput
                    mode="outlined"
                    value={search}
                    onChangeText={setSearch}
                    placeholder={t("screens.collections.searchTagsPlaceholder")}
                    placeholderTextColor={theme?.colors?.primary}
                    textColor={theme?.colors?.primary}
                    returnKeyType="search"
                    style={styles.paperSearchField}
                    contentStyle={styles.paperSearchContent}
                    outlineStyle={styles.paperSearchOutline}
                  />
                </View>
              </GradientStrokeBox>
            </View>
          </View>

          <FlatList
            data={filtered}
            keyExtractor={(item: any, i) => `rec-${item.id}-${i}`}
            numColumns={GRID_COLUMNS}
            showsVerticalScrollIndicator={false}
            style={styles.listFlex}
            renderItem={renderItem}
            keyboardShouldPersistTaps="handled"
            columnWrapperStyle={
              GRID_COLUMNS > 1 ? styles.columnWrap : undefined
            }
            contentContainerStyle={[
              styles.listContent,
              {
                paddingBottom:
                  SAVE_FOOTER_RESERVED + Math.max(insets.bottom, 12),
              },
            ]}
            ListEmptyComponent={
              isLoading ? (
                <AppLoader isLoading />
              ) : (
                <Text
                  style={[
                    gStyles.body,
                    {
                      color: theme.colors.primary,
                      textAlign: "center",
                      marginTop: 24,
                    },
                  ]}
                >
                  {t("screens.collections.noRecommendationsMatch")}
                </Text>
              )
            }
          />

          <View
            style={[
              styles.saveFooterAbsolute,
              {
                paddingBottom: insets.bottom + 50,
                overflow: "hidden",
              },
            ]}
            pointerEvents="box-none"
          >
            <View
              pointerEvents="none"
              style={{
                position: "absolute",
                bottom: 0,
                left:
                  (Math.min(windowWidth, Number(MODAL_MAX_WIDTH) || windowWidth) -
                    windowWidth) /
                  2,
                width: windowWidth,
                height: windowHeight,
              }}
            >
              <CustomLinearGradient
                gradientType={
                  showWebVersion ? GRADIENT_TYPE.BASE_WEB : GRADIENT_TYPE.BASE
                }
              />
            </View>
            <CustomButton
              variant={ButtonVariant.NORMAL_PRIMARY}
              disabled={!canSave || isSaving}
              onPress={onSave}
              style={styles.saveWide}
            >
              {isSaving
                ? t("screens.collections.saving")
                : t("screens.collections.save")}
            </CustomButton>
          </View>
      </View>
    </AppBackground>
  );
}

const styles = StyleSheet.create({
  screenGradient: {
    flex: 1,
    width: "100%",
  },
  contentSheet: {
    flex: 1,
    maxWidth: "100%",
    borderTopLeftRadius: 22,
    borderTopRightRadius: 22,
    width: MODAL_MAX_WIDTH, 
    alignSelf: "center"
  },
  searchSection: {
    flexShrink: 0,
    width: "100%",
    paddingHorizontal: 20,
    paddingTop: 12,
  },
  listFlex: {
    flex: 1,
    minHeight: 0,
  },
  searchInputWrap: {
    width: "100%",
    paddingTop: 6,
    marginBottom: 16,
  },
  searchGradientWrap: {
    width: "100%",
    minHeight: 56,
    borderRadius: 30,
  },
  searchInputInner: {
    flex: 1,
    position: "relative",
  },
  paperSearchField: {
    flex: 1,
    height: 56,
    backgroundColor: "transparent",
  },
  paperSearchContent: {
    height: 56,
  },
  paperSearchOutline: {
    borderWidth: 0,
  },
  listContent: {
    paddingHorizontal: 20,
    paddingTop: 4,
    flexGrow: 1,
  },
  columnWrap: {
  gap: 8,
  justifyContent: "flex-start",
  marginBottom: 12,
  },
  cell: {
    alignItems: "center",
    marginBottom: 4,
  },
  bubbleWithBadge: {
    position: "relative",
    alignItems: "center",
    marginLeft: 10,
  },
  selectionBadge: {
    position: "absolute",
    left: "50%",
    width: 30,
    height: 30,
    borderRadius: 50,
    justifyContent: "center",
    alignItems: "center",
    borderWidth: 2,
    zIndex: 10,
  },
  saveFooterAbsolute: {
    position: "absolute",
    left: 0,
    right: 0,
    bottom: 0,
    zIndex: 20,
    paddingHorizontal: 20,
    paddingTop: 12,
  },
  saveWide: {
    width: "100%",
    minHeight: 48,
  },
});
